// src/components/products/SupportSection.jsx
import React from "react";
import { PlayIcon, PhotoIcon } from "@heroicons/react/24/outline";

const SupportSection = ({ title, videos, photos, safeText, onMediaClick }) => {
  const hasVideos = videos && videos.length > 0;
  const hasPhotos = photos && photos.length > 0;

  if (!title || (!hasVideos && !hasPhotos)) return null;

  const handleVideoClick = (video, index) => {
    const youtubeId = video.youtube_id || video.youtubeId;
    const videoSubtitle = safeText(
      video.subtitle ||
        video.videoTitle ||
        `Video ${index + 1}`
    );

    if (!youtubeId || !onMediaClick) return;

    onMediaClick({
      type: "video",
      title: videoSubtitle,
      youtubeId,
    });
  };

  const handlePhotoClick = (item, index) => {
    const image = item.image || item;
    const imageUrl =
      item.image?.formats?.thumbnail?.url ||
      item.image?.url;
    if (!imageUrl) return;

    const photoSubtitle = safeText(
      item.subtitle ||
        item.photoTitle ||
        image?.alternativeText ||
        `Photo ${index + 1}`
    );

    const imagePdf = item.image_pdf;
    const pdfUrl = imagePdf?.url;

    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!onMediaClick) return;

    onMediaClick({
      type: "photo",
      title: photoSubtitle,
      imageUrl,
    });
  };

  return (
    <div className="mb-12 md:mb-16">
      <div className="product-panel-elevated rounded-3xl shadow-2xl overflow-hidden">
        {/* Support Title Bar */}
        <div className="product-panel-header">
          <h2>
            {safeText(title)}
          </h2>
        </div>

        {/* Support Content */}
        <div className="p-6 sm:p-8 lg:p-10">
          {/* Videos Section */}
          {hasVideos && (
            <div className="mb-8 md:mb-12">
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <PlayIcon className="h-6 w-6 product-support-icon" />
                <h3 className="text-xl md:text-2xl font-semibold product-support-heading">
                  Videos
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
                {videos.map((video, videoIndex) => {
                  const youtubeId = video.youtube_id || video.youtubeId;
                  const videoSubtitle = safeText(
                    video.subtitle ||
                      video.videoTitle ||
                      `Video ${videoIndex + 1}`
                  );

                  if (!youtubeId) return null;

                  return (
                    <button
                      key={video.id || videoIndex}
                      type="button"
                      className="text-left focus:outline-none group flex flex-col h-full"
                      onClick={() => handleVideoClick(video, videoIndex)}
                    >
                      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300">
                        <img
                          src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                          alt={videoSubtitle}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors duration-300">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <PlayIcon className="h-8 w-8 text-green-600 ml-1" />
                          </div>
                        </div>
                      </div>
                      <p className="mt-2 text-sm md:text-base font-medium product-download-title line-clamp-2">
                        {videoSubtitle}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Photos Section */}
          {hasPhotos && (
            <div>
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <PhotoIcon className="h-6 w-6 product-support-icon" />
                <h3 className="text-xl md:text-2xl font-semibold product-support-heading">
                  Photos
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
                {photos.map((item, photoIndex) => {
                  const image = item.image || item;
                  const imageUrl =
                    item.image?.formats?.thumbnail?.url ||
                    item.image?.url;
                  if (!imageUrl) return null;

                  const photoSubtitle = safeText(
                    item.subtitle ||
                      item.photoTitle ||
                      image?.alternativeText ||
                      `Photo ${photoIndex + 1}`
                  );

                  const imagePdf = item.image_pdf;
                  const pdfUrl = imagePdf?.url;

                  return (
                    <button
                      key={item.id || image?.id || photoIndex}
                      type="button"
                      className="text-left focus:outline-none group"
                      onClick={() => handlePhotoClick(item, photoIndex)}
                    >
                      <div className="relative w-full aspect-video overflow-hidden rounded-xl shadow-lg group-hover:shadow-2xl transition-all duration-300">
                        <img
                          src={imageUrl}
                          alt={
                            image?.alternativeText ||
                            photoSubtitle ||
                            "Support"
                          }
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {pdfUrl && (
                          <div className="absolute top-2 right-2 product-badge px-2 py-1 rounded text-xs font-semibold">
                            PDF
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-sm md:text-base font-medium product-download-title line-clamp-2">
                        {photoSubtitle}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportSection;





